import { cn } from "@base/lib/utils";
import { useDebounce } from "@base/model/hooks";
import { FullPageSheet } from "@base/ui/FullPageSheet";
import { Button } from "@buildblockinc/buildblock-design-system";
import type { ProjectTagItem } from "@features/write-daily-log/model/projectTagsSchema";
import type { Tag } from "@features/write-daily-log/model/schema";
import { useCreateProjectTagMutation } from "@features/write-daily-log/model/useCreateProjectTagMutation";
import { useProjectTagsQuery } from "@features/write-daily-log/model/useProjectTagsQuery";
import { SearchBar } from "@features/write-daily-log/ui/SearchBar";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";

const SKELETON_WIDTHS = [45, 60, 35, 55, 40];

interface OverviewStatusSheetProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  currentTags: Tag[];
  onPickCatalogItem: (item: ProjectTagItem) => void;
  onRemove: (tagId: number) => void;
}

export function OverviewStatusSheet({
  projectId,
  isOpen,
  onClose,
  currentTags,
  onPickCatalogItem,
  onRemove,
}: OverviewStatusSheetProps) {
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 300);

  const { data, isPending, isError } = useProjectTagsQuery(projectId, debouncedKeyword, {
    enabled: isOpen && !!projectId,
  });

  const createTag = useCreateProjectTagMutation(projectId, {
    onSuccess: (created) => {
      onPickCatalogItem(created);
      queryClient.invalidateQueries({ queryKey: ["project-tags", projectId] });
    },
    onSettled: () => {
      setKeyword("");
    },
  });

  const handleClose = () => {
    setKeyword("");
    onClose();
  };

  const currentNames = useMemo(() => new Set(currentTags.map((t) => t.name)), [currentTags]);

  const availableResults = useMemo(() => {
    if (!data?.results) return [];
    return data.results.filter((t) => !currentNames.has(t.name));
  }, [data, currentNames]);

  const trimmedDebounced = debouncedKeyword.trim();

  const resultNamesSet = useMemo(() => new Set(data?.results.map((t) => t.name) ?? []), [data]);

  const isCustomName = useMemo(
    () =>
      trimmedDebounced.length > 0 &&
      !currentNames.has(trimmedDebounced) &&
      !isPending &&
      !resultNamesSet.has(trimmedDebounced),
    [trimmedDebounced, currentNames, isPending, resultNamesSet],
  );

  let emptyMessage: string | null = null;
  if (isError) emptyMessage = "Could not load status list.";
  else if (data?.results.length === 0) emptyMessage = "No status found.";

  return (
    <FullPageSheet
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel="Select status"
      className={cn(
        "md:top-0 md:left-auto md:w-[440px] md:max-w-full md:rounded-none",
        "md:border-l md:border-gray-200 md:shadow-[-4px_0_25px_rgba(0,0,0,0.12)]",
        isOpen ? "md:translate-x-0" : "md:translate-x-full md:translate-y-0",
      )}
    >
      <div className="p-4">
        <SearchBar value={keyword} onChange={setKeyword} onBack={handleClose} disabled={createTag.isPending} />

        {currentTags.length > 0 && (
          <div className="flex flex-col gap-y-2 mt-4">
            <h2 className="text-gray-900 text-title6">Current status</h2>
            <ul className="flex flex-col gap-y-2">
              {currentTags.map((tag) => (
                <li key={tag.id} className="flex gap-x-2 items-center p-4 bg-blue-10 rounded-8">
                  <span className="flex-1 min-w-0 text-gray-900 truncate text-label2">{tag.name}</span>
                  <button type="button" onClick={() => onRemove(tag.id)} aria-label={`Remove ${tag.name}`}>
                    <X width={20} height={20} className="text-gray-500 cursor-pointer" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isOpen && !!projectId && isPending && (
          <div className="flex flex-col gap-y-2 mt-4">
            <ul className="flex flex-col gap-y-2">
              {SKELETON_WIDTHS.map((width) => (
                <li key={width} className="flex justify-between items-center p-3">
                  <div className="h-4 rounded-4 skeleton-shimmer" style={{ width: `${width}%` }} />
                  <div className="w-5 h-5 rounded-4 skeleton-shimmer shrink-0" />
                </li>
              ))}
            </ul>
          </div>
        )}

        {isOpen && !!projectId && !isPending && !isCustomName && emptyMessage && (
          <p className="mt-4 text-center text-gray-600 text-label3">{emptyMessage}</p>
        )}

        {isCustomName && (
          <Button
            variant="ghost"
            color="secondary"
            size="large"
            className="!justify-start w-full mt-4"
            disabled={createTag.isPending}
            onClick={() => createTag.mutate({ name: trimmedDebounced })}
          >
            Add "{trimmedDebounced}"
          </Button>
        )}
        {availableResults.length > 0 && (
          <div className="flex flex-col gap-y-2 mt-4">
            <h2 className="p-3 pb-0 text-gray-600 text-label2">Available status</h2>
            <ul className="flex flex-col gap-y-2">
              {availableResults.map((status) => (
                <li key={status.id} className="flex justify-between items-center p-2 pl-3">
                  <span className="text-gray-900 text-label2">{status.name}</span>
                  <button
                    type="button"
                    onClick={() => onPickCatalogItem(status)}
                    aria-label={`Add ${status.name}`}
                    className="p-1"
                  >
                    <Plus width={20} height={20} className="text-gray-900 cursor-pointer" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </FullPageSheet>
  );
}
